import * as React from "react";
import { Lambda, observable, observe, toJS } from "mobx";
import { observer } from "mobx-react";
import { resolve } from "inversify-react";
import { Col, Row, Popover } from "antd";
import * as uuid from "uuid";

import { FormatService } from "src/core/services/format.service";
import { CommandBar, CommandItem } from "src/core/ui/command-bar";
import { ZuluIcon } from "src/app/shared/icon";
import { SearchOccupancyView } from "../search-occupancy.view";
import { CalculatePriceService } from "../../shared/calculate-prices.service";
import { ContractsAgeBandController } from "src/app/products/contract/form/parts/contract-ageband.controller";
import { ContractCustomOccupanciesView } from "src/app/products/contract/form/parts/contract-custom-occupancies.view";
import { TextView } from "src/core/ui/text.view";
import { CardController } from "src/app/shared/card.controller";
import { DataLocalService } from "src/core/services/data-local.service";
import { ToastNotifications } from "src/core/ui/toast";
import { ObjectUtils } from "src/core/utils/object";

interface Props {
	selection: ResultSelection;
	cancelConfig: () => void;
	setBusy: (isBusy: boolean) => void;
	search: Search;
	marketId: number;
	bookingDate: Date;
	languageCode: string;
	disableAddButton?: (val: boolean) => void;
}

interface State {
	type: SearchType;
}

interface TourOccupancy extends Occupancy {
	result: TourProductSearchResult;
	sellPrice: string;
}

interface TourConfig {
	result: TourProductSearchResult;
	sellPrice: string;
	occupancies: TourOccupancy[];
	hasChanges: boolean;
}

@observer
export class SearchResultTourItineraryConfig extends React.Component<Props, State> {
	public state: State = { type: "Accommodation" };

	private subscription: Lambda[] = [];

	@resolve
	private format: FormatService;

	@resolve
	private toast: ToastNotifications;

	@resolve
	private calculatePriceService: CalculatePriceService;

	@observable
	private data: TourConfig[];

	public componentDidMount() {
		const { type } = this.state;
		this.updateProductsOccupancies(type);
	}

	public componentWillUnmount() {
		for (const sub of this.subscription) {
			sub();
		}
	}

	public render(): React.ReactNode {
		if (this.data == null || this.data.length === 0) {
			return null;
		}

		const { type = "Accommodation" } = this.state;
		const { cancelConfig, selection, setBusy, search, children } = this.props;

		let typeColumn: string;
		switch (type) {
			case "Accommodation":
				typeColumn = "Accommodation/Room";
				break;
			case "CarRent":
				typeColumn = "Car Rent";
				break;
			case "Transfer":
				typeColumn = "Transfer";
				break;
			case "Flights":
				typeColumn = "Flight";
				break;
			case "General":
				typeColumn = "General";
				break;
		}
		const tourResult = selection.selected.result as TourSearchResult;
		const body = this.data.map(p => {
			const columns = [
				{
					key: "result",
					dataIndex: "result",
					title: "",
					render: (product: TourProductSearchResult, record: TourOccupancy) => {
						const maxPax = product.maxPax;
						const ageband = product.ageBands;
						const occupancyDataSet: DataSet<CustomOccupancyPax> = product.customOccupancyConfigs
							? new DataLocalService(product.customOccupancyConfigs)
							: null;
						const content = (
							<React.Fragment>
								<ContractsAgeBandController
									mode="display"
									model={ageband}
									setBusy={this.props.setBusy}
								/>
								<CardController icon='setting' title="Paxs config" mode="display" className='card-borderless'>
									<TextView
										mode="display"
										label='Max Pax'
										value={maxPax}
										required={true}
									/>
									{
										ageband && ageband.length > 0 && occupancyDataSet && <ContractCustomOccupanciesView
											ageBands={ageband.map(e => e.label)}
											mode="display"
											model={maxPax}
											occupancyDataSet={occupancyDataSet}
											setBusy={this.props.setBusy}
										/>
									}
								</CardController>
							</React.Fragment>
						);
						const tour = this.props.selection.selected.result as TourSearchResult;
						return (
							<React.Fragment>
								{!(this.checkOccupancy(tour, product, record.paxs) && this.checkPrice(tour, product)) && <ZuluIcon type="warning" theme="twoTone" twoToneColor="#eb2f96" />}
								<Popover
									content={content}
									title={product.product.name}
									trigger="click"
									placement="right"
									arrowPointAtCenter={true}
								>
									<ZuluIcon type="setting" />
								</Popover>
							</React.Fragment>
						);
					}
				},
				{
					key: typeColumn,
					dataIndex: "result.product.name",
					title: typeColumn
				},
				{
					key: "sellPrice",
					dataIndex: "sellPrice",
					title: "Sell Price"
				},
				{
					key: "startDate",
					dataIndex: "result.startDate",
					title: "From",
					render: (text) => this.format.toString("small-date-time", text),
				},
				{
					key: "endDate",
					dataIndex: "result.endDate",
					title: "To",
					render: (text) => this.format.toString("small-date-time", text),
				},
			];
			if (tourResult.features.isPackage) {
				columns.splice(2);
			}
			return (
				<div className="mt-2 mb-2" key={p.result.id}>
					<SearchOccupancyView
						extraToolbarCmd={[
							{
								name: tourResult.features.isPackage ? "Update Occupancies" : "Refresh Price",
								key: "price",
								icon: "pound",
								onClick: this.refreshPrice(p),
								disabled: !p.hasChanges
							}
						]}
						extraColumns={columns}
						occupancies={p.occupancies}
						paxs={search.occupancies.map(e => e.paxs).reduce((prev, curr) => prev.concat(curr)).slice()}
						setBusy={setBusy}
						type={type}
					/>
				</div>
			);
		});
		const tour = selection.selected.result as TourSearchResult;
		const subProducts = (tour)
			.subProducts
			.map(e => ({
				type: e.productType,
				hasPrice: Object.getOwnPropertyNames(e.sellPrice).length > 0,
				isValidOccupancy: this.checkOccupancy(tour, e) && this.checkPrice(tour, e)
			}));
		const generalProducts = subProducts.filter(e => e.type === 0);
		const generalProductsValid = generalProducts.some(e => !e.isValidOccupancy);
		const accommodationProducts = subProducts.filter(e => e.type === 1 || e.type === 5);
		const accommodationProductsValid = accommodationProducts.some(e => !e.isValidOccupancy)
		const carRentProducts = subProducts.filter(e => e.type === 2);
		const carRentProductsValid = carRentProducts.some(e => !e.isValidOccupancy);
		const transferProducts = subProducts.filter(e => e.type === 3);
		const transferProductsValid = transferProducts.some(e => !e.isValidOccupancy);
		const flightProducts = subProducts.filter(e => e.type === 4);
		const flightProductsValid = flightProducts.some(e => !e.isValidOccupancy);
		const cmds = [];
		if (generalProducts.length > 0) {
			cmds.push(
				{
					key: 'General',
					name: <React.Fragment> General {generalProductsValid && <ZuluIcon type="warning" theme="twoTone" twoToneColor="#eb2f96" />}</React.Fragment>,
					icon: 'tags',
					onClick: this.showProductView("General"),
					type: type && type === "General" ? "primary" : undefined
				}
			);
		}
		if (accommodationProducts.length > 0) {
			cmds.push(
				{
					key: 'Accommodation',
					name: <React.Fragment> Accommodation {accommodationProductsValid && <ZuluIcon type="warning" theme="twoTone" twoToneColor="#eb2f96" />}</React.Fragment>,
					icon: 'icon-hotel',
					onClick: this.showProductView("Accommodation"),
					type: type && type === "Accommodation" ? "primary" : undefined
				}
			);
		}
		if (carRentProducts.length > 0) {
			cmds.push(
				{
					key: 'CarRent',
					name: <React.Fragment> Car Rent {carRentProductsValid && <ZuluIcon type="warning" theme="twoTone" twoToneColor="#eb2f96" />}</React.Fragment>,
					icon: 'car',
					onClick: this.showProductView("CarRent"),
					type: type && type === "CarRent" ? "primary" : undefined
				}
			);
		}
		if (transferProducts.length > 0) {
			cmds.push(
				{
					key: 'Transfer',
					name: <React.Fragment> Transfer {transferProductsValid && <ZuluIcon type="warning" theme="twoTone" twoToneColor="#eb2f96" />}</React.Fragment>,
					icon: 'icon-train',
					onClick: this.showProductView("Transfer"),
					type: type && type === "Transfer" ? "primary" : undefined
				}
			);
		}
		if (flightProducts.length > 0) {
			cmds.push(
				{
					key: 'Flights',
					name: <React.Fragment> Flights {flightProductsValid && <ZuluIcon type="warning" theme="twoTone" twoToneColor="#eb2f96" />}</React.Fragment>,
					icon: 'icon-plane',
					onClick: this.showProductView("Flights"),
					type: type && type === "Flights" ? "primary" : undefined
				}
			);
		}
		const buttons: CommandItem[] = [
			{
				key: 'close',
				name: 'Close occupancy config',
				icon: 'close',
				onClick: cancelConfig,
				type: "danger"
			},
		];
		const cmd = (
			<CommandBar
				mode="edit"
				items={cmds}
				farItems={buttons}
			/>
		);

		return (
			<div className="card card-borderless">
				<Row>
					<Col span={24}>
						{cmd}
					</Col>
				</Row>
				<Row>
					<Col span={24}>
						{body}
					</Col>
				</Row>
			</div>
		);
	}

	private showProductView = (type: SearchType) => () => {
		this.setState({ type: type });
		this.updateProductsOccupancies(type);
	};

	private refreshPrice = (config: TourConfig) => async () => {
		const { bookingDate, languageCode, marketId, search, selection, setBusy } = this.props;
		const { type } = this.state;

		const tourResult = selection.selected.result as TourSearchResult;
		if (!tourResult.features.isPackage) {
			const product = config.result;
			const preferences: SearchPreferences = {
				criteria: "Price",
				direction: "Asc",
				pageIndex: 0,
				pageSize: 10
			};
			const baseSearch: (Search & SearchDates) = {
				apiNames: search.apiNames,
				isActive: search.isActive,
				occupancies: config.occupancies,
				productId: product.product.internalId,
				productExternalId: product.product.externalId,
				type: "General",
				searchInCache: false,
				searchId: uuid.v4(),
				facets: [],
				startDate: product.startDate,
				endDate: product.endDate,
				useSellMarginPolicies: true,
			};
			let results: ApiResult;
			try {
				setBusy(true);
				switch (product.productType) {
					case 0:
						results = await this.calculatePriceService.searchGeneral(
							baseSearch,
							marketId,
							bookingDate,
							languageCode,
							preferences
						);
						break;
					case 1:
					case 5:
						results = await this.calculatePriceService.searchAccommodation(
							{ ...baseSearch, type: "Accommodation", nights: 0, bypassOcuppancy: false, },
							marketId,
							bookingDate,
							languageCode,
							preferences
						);
						break;
					case 2:
						results = await this.calculatePriceService.searchCarRent(
							{ ...baseSearch, type: "CarRent", days: 0 },
							marketId,
							bookingDate,
							languageCode,
							preferences
						);
						break;
					case 3:
						results = await this.calculatePriceService.searchTransfer(
							{ ...baseSearch, type: "Transfer", returnJourney: false },
							marketId,
							bookingDate,
							languageCode,
							preferences
						);
						break;
					case 4:
						const flightResult = product as FlightSearchResult;
						results = await this.calculatePriceService.searchFlight(
							{
								...baseSearch,
								type: "Flights",
								flights: flightResult.legs.map(l => ({
									id: '',
									fromPlaceId: l.departureAirport.internalId,
									toPlaceId: l.destinationAirport.internalId,
									startDate: l.startDate,
									endDate: l.endDate,
								})),
								publicFares: true,
								negociatedFares: true,
								lastTicketingDate: new Date(),
							},
							marketId,
							bookingDate,
							languageCode,
							preferences
						);
						break;
				}
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				setBusy(false);
			}
			if (results.items.length > 0) {
				let index = selection.selected.result.subProducts.findIndex((i) => i.id === config.result.id);
				if (index === -1) {
					index = 0;
					for (const product of selection.selected.result.subProducts) {
						if (product.subProducts.length > 0 && product.subProducts.some(e => e.id === config.result.id)) {
							break;
						}
						index++;
					}
				}
				for (let item of results.items) {
					let product = item;
					let props = Object.getOwnPropertyNames(toJS(product.sellPrice));
					if (props.length === 0 && product.subProducts.length === 1) {
						product = item.subProducts[0];
					}
					if (config.result.isRequired) {
						product.isRequired = true;
					}
				}
				selection.selected.result.subProducts.splice(index, 1, ...results.items);
				this.updateProductsOccupancies(type);
				config.hasChanges = false;
			}
		} else {
			const index = selection.selected.result.subProducts.findIndex((i) => i.id === config.result.id);
			/*const results = */this.convertOccupancyToResults(config);
			selection.selected.result.subProducts.splice(index, 1, config.result);
			this.updateProductsOccupancies(type);
			config.hasChanges = false;
		}
	}

	private convertOccupancyToResults(config: TourConfig): void {
		const tour = this.props.selection.selected.result as TourSearchResult;
		config.result.costPrice = {};
		config.result.sellPrice = {};
		for (const occupancy of config.occupancies) {
			const paxs = occupancy.paxs.map(e => e.id);
			config.result.costPrice[occupancy.unit] = {
				currencyIsoCode: tour.costPrice[1].currencyIsoCode,
				exchangeRate: 1,
				isPriceLive: true,
				isCommissionable: false,
				sourceCurrencyIsoCode: tour.costPrice[1].currencyIsoCode,
				totalPrice: 0,
				breakdown: null,
				paxs: paxs,
			};
			config.result.sellPrice[occupancy.unit] = {
				currencyIsoCode: tour.sellPrice[1].currencyIsoCode,
				exchangeRate: 1,
				isPriceLive: true,
				isCommissionable: false,
				sourceCurrencyIsoCode: tour.sellPrice[1].currencyIsoCode,
				totalPrice: 0,
				breakdown: null,
				paxs: paxs,
			};
		}
	}

	private updateProductsOccupancies(type: SearchType): void {
		const { search, selection } = this.props;

		const data: TourConfig[] = [];
		for (let item of selection.selected.result.subProducts) {
			switch (item.productType) {
				case 0:
					if (type !== "General") {
						continue;
					}
					break;
				case 1:
				case 5:
					if (type !== "Accommodation") {
						continue;
					}
					break;
				case 2:
					if (type !== "CarRent") {
						continue;
					}
					break;
				case 3:
					if (type !== "Transfer") {
						continue;
					}
					break;
				case 4:
					if (type !== "Flights") {
						continue;
					}
					break;
			}

			let props = Object.getOwnPropertyNames(toJS(item.sellPrice));
			if (props.length === 0 && item.subProducts.length === 1) {
				item = item.subProducts[0];
				props = Object.getOwnPropertyNames(toJS(item.sellPrice));
			}
			if (props.length > 0) {
				for (const unitProp of props) {
					const unit = parseInt(unitProp);
					let sellPrice = item.sellPrice[unit].totalPrice + " " + item.sellPrice[unit].currencyIsoCode;
					const paxs = search.occupancies[0].paxs.filter(p => item.sellPrice[unit].paxs.some(i => i === p.id)).slice();

					const oldItem = data.find(
						p => p.result.product.name === item.product.name
							&& p.result.startDate === item.startDate
							&& p.result.endDate === item.endDate
					);

					if (oldItem == null) {
						data.push({
							result: item,
							sellPrice,
							occupancies: [
								{
									unit: unit,
									paxs: paxs,
									result: item,
									sellPrice
								}
							],
							hasChanges: false,
						});
					} else {
						oldItem.occupancies.push({
							unit: unit,
							paxs: paxs,
							result: item,
							sellPrice
						});
					}
				}
			} else {
				data.push({
					result: item,
					sellPrice: "",
					occupancies: [
						{
							unit: 1,
							paxs: search.occupancies[0].paxs.slice(),
							result: item,
							sellPrice: "",
						}
					],
					hasChanges: false,
				});
			}
		}
		this.data = data;
		this.subscription = [];
		for (const d of this.data) {
			this.subscription.push(observe(d.occupancies, this.onChange(d)));
			for (const occ of d.occupancies) {
				this.subscription.push(observe(occ.paxs, this.onChange(d)));
			}
		}
		this.disableAddProductButton();
	}

	private onChange = (config: TourConfig) => () => {
		config.hasChanges = true;
	};

	private checkOccupancy(tour: TourSearchResult, product: TourProductSearchResult, paxs?: PersonOccupancy[]): boolean {
		if (paxs) {
			if (product.maxPax && paxs.length > product.maxPax) {
				return false;
			}
			if (
				product.customOccupancyConfigs == null
				|| product.customOccupancyConfigs.length === 0
				|| product.ageBands == null
				|| product.ageBands.length === 0
			) {
				return !product.maxPax || paxs.length <= product.maxPax;
			}
			for (const occupancy of product.customOccupancyConfigs) {
				let isValid = true;
				for (const ageBand of product.ageBands) {
					const paxCount = paxs.filter(p => ageBand.minAge <= p.age && p.age <= ageBand.maxAge);
					isValid = isValid && (paxCount.length <= parseInt(occupancy[ageBand.label.toLocaleLowerCase()]));
				}
				if (isValid) {
					return true;
				}
			}
		} else {
			let props = Object.getOwnPropertyNames(toJS(product.sellPrice));
			if (props.length === 0 && product.subProducts.length === 1) {
				product = product.subProducts[0];
				props = Object.getOwnPropertyNames(toJS(product.sellPrice));
			}
			let isValid = true;
			if (props.length > 0) {
				for (const unitProp of props) {
					const unit = parseInt(unitProp);
					const unitPrice = product.sellPrice[unit];
					paxs = [];
					for (const occupancy of this.props.search.occupancies) {
						const paxsInOccupancy = occupancy.paxs.filter(e => unitPrice.paxs.indexOf(e.id) !== -1);
						if (paxsInOccupancy.length > 0) {
							paxs.push(...paxsInOccupancy);
						}
					}
					isValid = this.checkOccupancy(tour, product, paxs);
					if (!isValid) {
						break;
					}
				}
			} else {
				const paxsIds = Object.getOwnPropertyNames(toJS(product.occupancy));
				paxs = [];
				for (const occupancy of this.props.search.occupancies) {
					const paxsInOccupancy = occupancy.paxs.filter(e => paxsIds.indexOf(e.id) !== -1);
					if (paxsInOccupancy.length > 0) {
						paxs.push(...paxsInOccupancy);
					}
				}
				isValid = this.checkOccupancy(tour, product, paxs);
			}
			return isValid;
		}
		return false;
	}

	private checkPrice(tour: TourSearchResult, product: TourProductSearchResult): boolean {
		const { search } = this.props;

		if (tour.features.isPackage) {
			return search.occupancies.length === Object.getOwnPropertyNames(toJS(tour.sellPrice)).length;
		}
		let props = Object.getOwnPropertyNames(toJS(product.sellPrice));
		if (props.length === 0 && product.subProducts.length === 1) {
			product = product.subProducts[0];
			props = Object.getOwnPropertyNames(toJS(product.sellPrice));
		}
		return props.length > 0;
	}

	private disableAddProductButton = (): void => {
		const { selection, disableAddButton } = this.props;
		const tour = selection.selected.result as TourSearchResult;

		const subProducts = (tour)
			.subProducts
			.map(e => ({
				type: e.productType,
				hasPrice: Object.getOwnPropertyNames(e.sellPrice).length > 0,
				isValidOccupancy: this.checkOccupancy(tour, e) && this.checkPrice(tour, e)
			}));
		const result = subProducts.some(e => !e.isValidOccupancy);
		if (disableAddButton) {
			disableAddButton(result);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/results/search-result-tour-itinerary-config.view.tsx